import React, { useState } from 'react';
import { Modal, Button, Table, Badge, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { OrderRequest, OrderRequestProduct } from '../../../../hooks/useOrderRequest';

interface RequestDetailViewProps {
  request: OrderRequest;
  onClose: () => void;
  products: OrderRequestProduct[];
}

export const RequestDetailView: React.FC<RequestDetailViewProps> = ({
  request,
  onClose,
  products,
}) => {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  const formatDate = (dateValue: any, dateOnly: boolean = false): string => {
    if (!dateValue) return 'N/A';
    try {
      let date;
      if (dateValue?.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        date = new Date(dateValue);
      }
      
      if (dateOnly) {
        // Return only the date part without time
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
      }
      
      return date.toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  };
  
  // Format amount in LKR (Sri Lankan Rupees)
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount).replace('LKR', 'Rs.');
  };
  
  const handleGoToApprovals = () => {
    navigate('/allocation/allocation-process', { 
      state: { 
        requestId: request.id, 
        requestData: request, 
        products: products,
        activeTab: 'approvals' // This will tell Allocation component to show Approvals tab
      }
    });
    onClose();
  };
  
  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };
  
  return (
    <>
      <Modal show={true} onHide={onClose} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            Request Details
            <Badge bg={getStatusBadgeColor(request.status)} className="ms-2">
              {request.status}
            </Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}
          
          <Row className="mb-4">
            <Col md={6}>
              <h6 className="fw-bold">Request Information</h6>
              <Table borderless size="sm">
                <tbody>
                  <tr>
                    <td className="text-muted">Type:</td>
                    <td>{request.requestType}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Department:</td>
                    <td>{request.department}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Requested By:</td>
                    <td>{request.requestedBy}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Created At:</td>
                    <td>{formatDate(request.createdAt)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Target Date:</td>
                    <td className="fw-semibold">{formatDate(request.targetDate, true)}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Total Amount:</td>
                    <td className="fw-bold text-primary">{formatAmount(request.totalAmount)}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <h6 className="fw-bold">Summary</h6>
              <Table borderless size="sm">
                <tbody>
                  <tr>
                    <td className="text-muted">Total Items:</td>
                    <td className="fw-semibold">{request.totalItems}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Total Quantity:</td>
                    <td className="fw-semibold">{request.totalQuantity}</td>
                  </tr>
              
                  {request.notes && (
                    <tr>
                      <td className="text-muted">Notes:</td>
                      <td>{request.notes}</td>
                    </tr>
                  )}
                  {request.approvedBy && (
                    <tr>
                      <td className="text-muted">Approved By:</td>
                      <td>{request.approvedBy}</td>
                    </tr>
                  )}
                  {request.approvedAt && (
                    <tr>
                      <td className="text-muted">Approved At:</td>
                      <td>{formatDate(request.approvedAt)}</td>
                    </tr>
                  )}
                  {request.rejectedBy && (
                    <tr>
                      <td className="text-muted">Rejected By:</td>
                      <td>{request.rejectedBy}</td>
                    </tr>
                  )}
                  {request.rejectionReason && (
                    <tr>
                      <td className="text-muted">Rejection Reason:</td>
                      <td className="text-danger">{request.rejectionReason}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
          
          <h6 className="fw-bold mb-3">Products</h6>
          <Table responsive bordered hover size="sm">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Product Name</th>
                <th>Type</th>
                <th>Price (LKR)</th>
                <th>Quantity</th>
                <th>Total (LKR)</th>
                <th>Tier</th>
              </tr>
            </thead>
            <tbody>
              {products && products.length > 0 ? (
                products.map((product, idx) => (
                  <tr key={product.itemId || idx}>
                    <td>{idx + 1}</td>
                    <td className="fw-semibold">{product.displayName}</td>
                    <td>
                      <Badge bg="info">{product.itemType || 'N/A'}</Badge>
                    </td>
                    <td>{formatAmount(product.price)}</td>
                    <td>{product.quantity}</td>
                    <td className="fw-bold">{formatAmount(product.price * product.quantity)}</td>
                    <td>
                      {product.tier && <Badge bg="secondary">{product.tier}</Badge>}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    No products found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGoToApprovals}
          >
            Go to Approvals
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};